"use client";

import type { ResolvedNavLink } from "@/lib/navigation/types";
import { cn } from "cn";
import type { Route } from "next";
import Link from "next/link";
import { Fragment } from "react";
import { useNavLinks } from "../sidebar-v2/sidebar-body";

/** The pages of wherever you are, as tabs. The current one is underlined in ink. */
export function SectionTabs() {
  const links = useNavLinks();

  return (
    <nav
      aria-label="Section"
      className="flex h-11 items-stretch overflow-x-auto border-t px-2 scrollbar-hide md:px-3 2xl:h-13 2xl:border-t-0 2xl:px-0"
    >
      {links.map((link) => (
        <Fragment key={link.key}>
          {link.separatorAbove ? (
            <span aria-hidden="true" className="mx-2 my-auto h-4 w-px shrink-0 bg-grayA-5" />
          ) : null}
          <SectionTab link={link} />
        </Fragment>
      ))}
    </nav>
  );
}

function SectionTab({ link }: { link: ResolvedNavLink }) {
  const className = cn(
    "relative flex shrink-0 items-center whitespace-nowrap px-2.5 text-sm transition-colors",
    "outline-hidden focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-(--focus-ring)",
    link.isActive
      ? "font-medium text-gray-12 after:absolute after:inset-x-2.5 after:-bottom-px after:h-0.5 after:rounded-full after:bg-gray-12"
      : "text-gray-11 hover:text-gray-12",
    link.disabled && "cursor-not-allowed opacity-50 hover:text-gray-11",
  );
  const contents = (
    <>
      <span>{link.label}</span>
      {link.tag ? <span className="ml-1.5">{link.tag}</span> : null}
    </>
  );

  if (link.disabled) {
    return (
      <span aria-disabled="true" className={className}>
        {contents}
      </span>
    );
  }

  return (
    <Link
      href={link.href as Route}
      aria-current={link.isActive ? "page" : undefined}
      className={className}
      {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {contents}
    </Link>
  );
}
